import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { DataGrid } from "@mui/x-data-grid";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchAcademicsOverview,
  editAcademicsOverview,
  updateAcademicsOverview,
  trashAcademicsOverview,
} from "../../Adapters/ApiAdapter";
import { baseImage_path, baseUrl } from "../../variables/Variables";
import CKEditorField from "../../components/CKEditorField";


const ListAcademicsOverview = () => {
  const [overview, setOverview] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchAcademicsOverview();
        setOverview([response.data]);
      } catch (err) {
        toast.error("Failed to fetch Academics Overview.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEdit = async (id) => {
    try {
      const response = await editAcademicsOverview(id);
      setEditItem(response.data);
    } catch (err) {
      toast.error("Failed to load data for editing.");
    }
  };

  const handleSaveEdit = async () => {
    setLoadingAction(true);

    const formData = new FormData();
    formData.append("heading", editItem.heading);
    formData.append("content", editItem.content);
    if (editItem.side_img instanceof File) {
      formData.append("side_img", editItem.side_img);
    }

    try {
      const response = await updateAcademicsOverview(editItem.id, formData);
      toast.success("Academics Overview updated successfully!");
      setOverview((prev) =>
        prev.map((item) =>
          item.id === editItem.id ? { ...item, ...response.data } : item
        )
      );
      setEditItem(null);
    } catch (err) {
      toast.error("Failed to save changes.");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDelete = async (id) => {
    setLoadingAction(true);
    try {
      await trashAcademicsOverview(id);
      toast.success("Academics Overview deleted successfully!");
      setOverview([]);
    } catch (err) {
      toast.error("Failed to delete Academics Overview.");
    } finally {
      setLoadingAction(false);
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 100 },
    { field: "heading", headerName: "Heading", width: 200 },
    { field: "content", headerName: "Content", width: 400 },
    {
      field: "side_img",
      headerName: "Image",
      width: 150,
      renderCell: (params) => (
        <Box>
          {params.row.side_img && (
            <img
              src={`${baseImage_path}/${params.row.side_img}`}
              alt="Side"
              style={{
                width: "60px",
                height: "60px",
                objectFit: "cover",
                borderRadius: "5px",
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row.id)}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row.id)}
            style={{ marginLeft: "8px" }}
          >
            Delete
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Academics Overview List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <DataGrid rows={overview.map((item) => ({ ...item, id: item.id }))} columns={columns} autoHeight />
      )}

      {/* Edit Dialog */}
      {editItem && (
        <Dialog open onClose={() => setEditItem(null)}>
          <DialogTitle>Edit Academics Overview</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              margin="normal"
              label="Heading"
              value={editItem.heading}
              onChange={(e) =>
                setEditItem({ ...editItem, heading: e.target.value })
              }
            />
            {/* <TextField
              fullWidth
              margin="normal"
              label="Content"
              multiline
              rows={4}
              value={editItem.content}
              onChange={(e) =>
                setEditItem({ ...editItem, content: e.target.value })
              }
            /> */}
            <Typography variant="body1" sx={{ mt: 2, mb: 1 }}>
              Content
            </Typography>
            {/* <ReactQuill
              theme="snow"
              value={editItem.content || ""}
              onChange={(value) =>
                setEditItem((prev) => ({ ...prev, content: value }))
              }
              style={{ height: "200px", marginBottom: "30px" }}
            /> */}
            <CKEditorField
            value={editItem.content}
            onChange={(data) => setEditItem((prev) => ({ ...prev, content: data }))}
            uploadUrl={`${baseUrl}api/ck-editor-upload?folder=academics`}
          />
            {editItem.side_img && !(editItem.side_img instanceof File) && (
              <Box mt={2}>
                <img
                  src={`${baseImage_path}/${editItem.side_img}`}
                  alt="Preview"
                  style={{
                    width: "100px",
                    height: "auto",
                    borderRadius: "5px",
                    marginTop: "10px",
                  }}
                />
              </Box>
            )}
            <Button variant="contained" component="label" sx={{ mt: 2 }}>
              Upload Side Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) =>
                  setEditItem({ ...editItem, side_img: e.target.files[0] })
                }
              />
            </Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              disabled={loadingAction}
            >
              {loadingAction ? <CircularProgress size={24} /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ListAcademicsOverview;